import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import LaunchIcon from "@mui/icons-material/Launch";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ThreatReviewModal from "../../modals/ThreatReviewModalTest.jsx";
import "./ThreatFeedTest.css";

const API_BASE_URL = "https://api.weathervaneintel.com/api";

const GET_GROUPS_URL = `${API_BASE_URL}/api/fetchGroups`;

const THREAT_FEED_COMMENTS_URL =
  `${API_BASE_URL}/api/threat-feed-comments`;


const THREAT_EVALUATION_URL =
  `${API_BASE_URL}/api/threateval`;

const FLAG_LABELS = {
  direct_threat: "Direct threat",
  violence: "Violence",
  planning: "Planning",
  location: "Location",
  grievance: "Grievance",
  hostility: "Hostility",
  weapons: "Weapons",
  protest: "Protest",
  event: "Event",
  coordination: "Coordination",
  exposure: "Exposure"
};

const FLAGS_JSON_PATH = "/protective_intelligence_flags.json";

const formatDate = (value) => {
  if (!value) return "Unknown date";

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return "Unknown date";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(parsedDate);
};

const parseCommaSeparatedValues = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const parseReasonFlags = (reasonFlags) =>
  parseCommaSeparatedValues(reasonFlags);

const parseMitigation = (value) => {
  if (!value) {
    return {
      selected: [],
      other: ""
    };
  }

  let parsed = value;

  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return {
        selected: [],
        other: ""
      };
    }
  }

  if (!parsed || typeof parsed !== "object") {
    return {
      selected: [],
      other: ""
    };
  }

  const selected = [];

  if (parsed.selectNoTarget) {
    selected.push("No specific target");
  }

  if (parsed.selectTooVague) {
    selected.push("Too vague");
  }

  if (parsed.select1A) {
    selected.push("Protected speech");
  }

  if (parsed.selectSarcasm) {
    selected.push("Sarcasm or exaggeration");
  }

  if (parsed.selectOther) {
    selected.push("Other");
  }

  return {
    selected,
    other: String(parsed.other || "")
  };
};

const normalizeComment = (comment) => {
  const mitigationData = parseMitigation(
    comment.mitigation
  );

  const evalType = String(
    comment.eval_type || ""
  ).trim();

  return {
    id: comment.comment_id,
    commentId: comment.comment_id,

    assetId: Number(comment.asset_id),

    assetName:
      comment.asset_type !== "person"
        ? (comment.asset_name || "Unnamed collection")
        : (
          comment.asset_name ||
          [comment.firstname, comment.lastname]
            .filter(Boolean)
            .join(" ") ||
          "Unnamed asset"
        ),

    assetType:
      comment.asset_type || "person",

    groupName:
      comment.group_name || "Unassigned group",

    username:
      comment.username || "Unknown",

    comment:
      comment.comment || "",

    datePosted:
      comment.date_posted,

    collectedAt:
      comment.collected_at,

    url:
      comment.link ||
      comment.search_url ||
      "",

    userUrl:
      comment.user_link || "",

    llmScore:
      Number(comment.threat_percent || 0),

    threatValue:
      Number(comment.threat_score || 0),

    flags:
      parseReasonFlags(comment.reason_flags),

    status:
      evalType || "Unreviewed",

    evalType:
      evalType || null,

    isReviewed:
      Boolean(evalType),

    concernReason:
      parseCommaSeparatedValues(
        comment.concern_reason
      ),

    mitigations:
      mitigationData.selected,

    analystNote:
      mitigationData.other,

    mitigation:
      comment.mitigation,

    evalDate:
      comment.eval_date,

    analyst:
      comment.analyst,

    directThreat:
      comment.direct_threat,

    isThreat:
      Boolean(comment.is_threat),

    legalHold:
      Boolean(comment.legal_hold),

    retentionCategory:
      comment.retention_category,

    retentionExpiration:
      comment.retention_expiration
  };
};

const threatBand = (value) => {
  if (value >= 30) return { label: "Priority review", className: "priority" };
  if (value >= 10) return { label: "Review", className: "review" };
  return { label: "Context", className: "context" };
};

const escapeRegex = (value) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function HighlightedComment({
  text,
  commentFlags = [],
  flagTerms = {}
}) {
  if (!text) {
    return null;
  }

  const matchedTerms = commentFlags
    .flatMap((flag) => {
      const terms = flagTerms[flag];

      return Array.isArray(terms)
        ? terms
        : [];
    })
    .map((term) => String(term).trim())
    .filter(Boolean);

  const uniqueTerms = Array.from(
    new Set(matchedTerms.map((term) => term.toLowerCase()))
  );

  if (uniqueTerms.length === 0) {
    return <>{text}</>;
  }

  // Match longer phrases before shorter words.
  const sortedTerms = uniqueTerms.sort(
    (a, b) => b.length - a.length
  );

  const escapedTerms = sortedTerms.map(escapeRegex);

  const matcher = new RegExp(
    `(?<!\\w)(${escapedTerms.join("|")})(?!\\w)`,
    "gi"
  );

  const parts = text.split(matcher);
  const normalizedTerms = new Set(sortedTerms);

  return (
    <span>
      {parts.map((part, index) => {
        const isFlaggedTerm = normalizedTerms.has(
          part.toLowerCase()
        );

        return isFlaggedTerm ? (
          <mark
            className="threat-term"
            key={`${part}-${index}`}
          >
            {part}
          </mark>
        ) : (
          <React.Fragment key={`${part}-${index}`}>
            {part}
          </React.Fragment>
        );
      })}
    </span>
  );
}

export const ThreatFeed = () => {
  const navigate = useNavigate();
  const [userToken, setUserToken] = useState(null);

  const [groupJson, setGroupJson] = useState([]);
  const [comments, setComments] = useState([]);

  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedAsset, setSelectedAsset] = useState("");
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  const [selectedAssetName, setSelectedAssetName] = useState("");
  const [selectedFeedScope, setSelectedFeedScope] = useState("");
  const [selectedFlag, setSelectedFlag] = useState("");
  const [unreviewedOnly, setUnreviewedOnly] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setHours(date.getHours() - 24);
    return date.toISOString().slice(0, 10);
  });

  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().slice(0, 10);
  });

  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [showFilters, setShowFilters] = useState(true);
  const [showFeed, setShowFeed] = useState(false);
  const [modalIndex, setModalIndex] = useState(-1);
  const [expandedCommentFlags, setExpandedCommentFlags] = useState(() => new Set());
  const [showAllFlags, setShowAllFlags] = useState(false);
  const [flagTerms, setFlagTerms] = useState({});

  const [groupsLoading, setGroupsLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [commentTotal, setCommentTotal] = useState(0);
  const [nextOffset, setNextOffset] = useState(null);

  const searchInputRef = useRef(null);

  // Replace this placeholder load with your authenticated API request later.
  useEffect(() => {
    const getUserToken = async () => {
      try {
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken?.toString();

        if (!accessToken) {
          navigate("/login");
          return;
        }

        setUserToken(accessToken);
      } catch (error) {
        console.error("Unable to retrieve authentication session:", error);
        navigate("/login");
      }
    };

    getUserToken();
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;

    const loadFlagTerms = async () => {
      try {
        const response = await fetch(FLAGS_JSON_PATH);

        if (!response.ok) {
          throw new Error(
            `Unable to load protective intelligence flags: ${response.status}`
          );
        }

        const data = await response.json();

        if (
          isMounted &&
          data &&
          typeof data === "object" &&
          !Array.isArray(data)
        ) {
          setFlagTerms(data);
        }
      } catch (error) {
        console.error(
          "Unable to load protective intelligence flag terms:",
          error
        );

        if (isMounted) {
          setFlagTerms({});
        }
      }
    };

    loadFlagTerms();

    return () => {
      isMounted = false;
    };
  }, []);

  const loadGroups = async () => {
    if (!userToken) return;

    setGroupsLoading(true);
    setErrorMessage("");

    try {
      const response = await fetch(GET_GROUPS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          access_token: userToken
        },
        body: JSON.stringify([{ userToken }])
      });

      if (response.status === 403) {
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error(`Get-groups request failed: ${response.status}`);
      }

      const data = await response.json();
      setGroupJson(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Unable to load monitoring groups:", error);
      setGroupJson([]);
      setErrorMessage("Unable to load monitoring groups.");
    } finally {
      setGroupsLoading(false);
    }
  };

  useEffect(() => {
    if (!userToken) return;
    loadGroups();
  }, [userToken]);

  const loadThreatFeedComments = async ({
    assetId = "all",
    assetIds = [],
    offset = 0,
    append = false,
    flag = selectedFlag,
    search = searchQuery,
    direction = sortDirection,
    onlyUnreviewed = unreviewedOnly,
    selectedStartDate = startDate,
    selectedEndDate = endDate
  }) => {
    if (!userToken) return;

    setCommentsLoading(true);
    setErrorMessage("");

    const {
      startDate: requestStartDate,
      endDate: requestEndDate
    } = getCommentDateWindow(
      selectedStartDate,
      selectedEndDate
    );

    try {
      const response = await fetch(
        THREAT_FEED_COMMENTS_URL,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify([
            {
              userToken,

              assetId:
                Array.isArray(assetIds) && assetIds.length > 0
                  ? null
                  : assetId === "all"
                    ? "all"
                    : Number(assetId),

              assetIds:
                Array.isArray(assetIds)
                  ? assetIds.map(Number).filter(Number.isFinite)
                  : [],

              startDate: requestStartDate,
              endDate: requestEndDate,

              pageSize: 100,
              offset,

              includeEvals: !onlyUnreviewed,
              reviewStatus:
                onlyUnreviewed
                  ? "unreviewed"
                  : "",

              searchQuery: search.trim(),

              flags: flag ? [flag] : [],
              flagMatch: "any",

              minimumThreatScore: null,

              sortField: "threatScore",
              sortDirection: direction
            }
          ])
        }
      );

      if (response.status === 403) {
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error(
          `Threat-feed comments request failed: ${response.status}`
        );
      }

      const data = await response.json();

      const normalizedComments = Array.isArray(data.comments)
        ? data.comments.map(normalizeComment)
        : [];

      setComments((currentComments) =>
        append
          ? [...currentComments, ...normalizedComments]
          : normalizedComments
      );

      setCommentTotal(
        Number(data.pagination?.total || 0)
      );

      setNextOffset(
        data.pagination?.nextOffset ?? null
      );

      setShowFeed(true);

      if (!append) {
        setModalIndex(-1);
      }
    } catch (error) {
      console.error("Unable to load selected asset comments:", error);

      setErrorMessage(
        "Unable to load comments for the selected asset."
      );

      if (!append) {
        setComments([]);
        setCommentTotal(0);
        setNextOffset(null);
      }
    } finally {
      setCommentsLoading(false);
    }
  };

  const groups = useMemo(() => {
    return groupJson
      .map((group) => {
        const members = Array.isArray(group.members)
          ? group.members
          : [];

        const assets = members
          .map((member) => {
            const type = member.assetType || "person";

            const name =
              type === "person"
                ? [member.firstname, member.lastname]
                  .filter(Boolean)
                  .join(" ") || "Unnamed person"
                : member.lastname ||
                member.firstname ||
                "Unnamed asset";

            return {
              id: Number(member.id_num),
              name,
              type,
              searchTermCount:
                parseCommaSeparatedValues(
                  member.search_terms
                ).length
            };
          })
          .sort((a, b) =>
            a.name.localeCompare(b.name)
          );

        const mainGroupAssetId =
          group.mainGroupAssetId != null
            ? Number(group.mainGroupAssetId)
            : null;

        const memberAssetIds = Array.isArray(
          group.memberAssetIds
        )
          ? group.memberAssetIds
            .map(Number)
            .filter(Number.isFinite)
          : assets.map((asset) => asset.id);

        const allAssetIds = Array.isArray(
          group.allAssetIds
        )
          ? group.allAssetIds
            .map(Number)
            .filter(Number.isFinite)
          : [
            mainGroupAssetId,
            ...memberAssetIds
          ].filter(Number.isFinite);

        return {
          id: group.groupname || "Unassigned group",
          name: group.groupname || "Unassigned group",
          description:
            group.groupData ||
            group.mainGroup?.search_terms ||
            "",
          mainGroup: group.mainGroup || null,
          mainGroupAssetId,
          mainGroupSearchTermCount:
            parseCommaSeparatedValues(
              group.mainGroup?.search_terms ||
              group.groupData
            ).length,
          memberAssetIds,
          allAssetIds,
          assetCount: assets.length,
          searchTermCount: assets.reduce(
            (total, asset) =>
              total + asset.searchTermCount,
            0
          ),
          assets
        };
      })
      .sort((a, b) =>
        a.name.localeCompare(b.name)
      );
  }, [groupJson]);

  const flags = useMemo(
    () => Object.keys(FLAG_LABELS).map((flag) => [flag, null]),
    []
  );

  const totalAssets = useMemo(
    () => groups.reduce((total, group) => total + group.assetCount, 0),
    [groups]
  );

  const totalSearchTerms = useMemo(
    () => groups.reduce((total, group) => total + group.searchTermCount, 0),
    [groups]
  );

  const filteredComments = comments;

  const selectedGroupRecord = useMemo(
    () =>
      groups.find(
        (group) => group.id === selectedGroup
      ) || null,
    [groups, selectedGroup]
  );

  const isGroupFeedSelected =
    selectedFeedScope === "group-main" ||
    selectedFeedScope === "group-all";

  const isEntireGroupSelected =
    selectedFeedScope === "group-all";

  const getCommentDateWindow = (
    selectedStartDate = startDate,
    selectedEndDate = endDate
  ) => {
    const start = new Date(`${selectedStartDate}T00:00:00`);
    const end = new Date(`${selectedEndDate}T23:59:59.999`);

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    };
  };

  const clearFilters = () => {
    setSelectedFlag("");
    setSearchQuery("");
    setSortDirection("desc");
    const defaultEndDate = new Date();
    const defaultStartDate = new Date(
      defaultEndDate.getTime() - 24 * 60 * 60 * 1000
    );

    const defaultStartValue =
      defaultStartDate.toISOString().slice(0, 10);

    const defaultEndValue =
      defaultEndDate.toISOString().slice(0, 10);

    setStartDate(defaultStartValue);
    setEndDate(defaultEndValue);

    if (selectedAsset) {
      setUnreviewedOnly(false);

      loadThreatFeedComments({
        assetId:
          selectedFeedScope === "group-all"
            ? null
            : selectedAsset,

        assetIds:
          selectedFeedScope === "group-all"
            ? selectedAssetIds
            : [],
        offset: 0,
        append: false,
        flag: "",
        search: "",
        direction: "desc",
        onlyUnreviewed: false,
        selectedStartDate: defaultStartValue,
        selectedEndDate: defaultEndValue
      });
    }
  };

  const openFeedForGroup = (groupId) => {
    toggleGroup(groupId);
  };

  const openFeedForGroupComments = (
    group,
    scope = "group-all"
  ) => {
    const useEntireGroup =
      scope === "group-all";

    if (
      !useEntireGroup &&
      !group.mainGroupAssetId
    ) {
      setErrorMessage(
        `No group-level monitoring record is configured for ${group.name}.`
      );
      return;
    }

    if (
      useEntireGroup &&
      group.allAssetIds.length === 0
    ) {
      setErrorMessage(
        `No monitored records are configured for ${group.name}.`
      );
      return;
    }

    const defaultEndDate = new Date();

    const defaultStartDate = new Date(
      defaultEndDate.getTime() -
      24 * 60 * 60 * 1000
    );

    const defaultStartValue =
      defaultStartDate.toISOString().slice(0, 10);

    const defaultEndValue =
      defaultEndDate.toISOString().slice(0, 10);

    setSelectedGroup(group.id);
    setSelectedAsset(
      useEntireGroup
        ? `group:${group.id}`
        : group.mainGroupAssetId
    );
    setSelectedAssetIds(
      useEntireGroup
        ? group.allAssetIds
        : []
    );
    setSelectedAssetName(
      useEntireGroup
        ? `${group.name} · Entire Group`
        : `${group.name} · Group Terms`
    );
    setSelectedFeedScope(scope);

    setSelectedFlag("");
    setSearchQuery("");
    setSortDirection("desc");
    setUnreviewedOnly(false);
    setErrorMessage("");

    setStartDate(defaultStartValue);
    setEndDate(defaultEndValue);

    setComments([]);
    setCommentTotal(0);
    setNextOffset(null);
    setShowFeed(true);

    loadThreatFeedComments({
      assetId:
        useEntireGroup
          ? null
          : group.mainGroupAssetId,
      assetIds:
        useEntireGroup
          ? group.allAssetIds
          : [],
      offset: 0,
      append: false,
      flag: "",
      search: "",
      direction: "desc",
      onlyUnreviewed: false,
      selectedStartDate: defaultStartValue,
      selectedEndDate: defaultEndValue
    });

    setShowFilters(false);
  };

  const openFeedForAllAssets = () => {
    setSelectedGroup("all");
    setSelectedAsset("all");
    setSelectedAssetIds([]);
    setSelectedAssetName("All Assets");
    setSelectedFeedScope("all");

    setSelectedFlag("");
    setSearchQuery("");
    setSortDirection("desc");
    setUnreviewedOnly(false);

    setComments([]);
    setCommentTotal(0);
    setNextOffset(null);
    setShowFeed(true);

    loadThreatFeedComments({
      assetId: "all",
      offset: 0,
      append: false,
      flag: "",
      search: "",
      direction: "desc",
      onlyUnreviewed: false
    });

    setShowFilters(false);
  };

  const openFeedForAsset = (
    groupId,
    assetId,
    assetName
  ) => {
    setSelectedGroup(groupId);
    setSelectedAsset(assetId);
    setSelectedAssetIds([]);
    setSelectedAssetName(assetName);
    setSelectedFeedScope("asset");

    setSelectedFlag("");
    setSearchQuery("");
    setSortDirection("desc");
    setUnreviewedOnly(false);
    const defaultEndDate = new Date();
    const defaultStartDate = new Date(
      defaultEndDate.getTime() - 24 * 60 * 60 * 1000
    );

    const defaultStartValue =
      defaultStartDate.toISOString().slice(0, 10);

    const defaultEndValue =
      defaultEndDate.toISOString().slice(0, 10);

    setStartDate(defaultStartValue);
    setEndDate(defaultEndValue);

    setComments([]);
    setCommentTotal(0);
    setNextOffset(null);
    setShowFeed(true);

    loadThreatFeedComments({
      assetId,
      offset: 0,
      append: false,
      flag: "",
      search: "",
      direction: "desc",
      onlyUnreviewed: false,
      selectedStartDate: defaultStartValue,
      selectedEndDate: defaultEndValue
    });

    setShowFilters(false);
  };

  const handleUnreviewedToggle = (
    event
  ) => {
    const nextValue = event.target.checked;

    setUnreviewedOnly(nextValue);

    if (!selectedAsset) {
      return;
    }

    setComments([]);
    setCommentTotal(0);
    setNextOffset(null);

    loadThreatFeedComments({
      assetId:
        selectedFeedScope === "group-all"
          ? null
          : selectedAsset,

      assetIds:
        selectedFeedScope === "group-all"
          ? selectedAssetIds
          : [],
      offset: 0,
      append: false,
      flag: selectedFlag,
      search: searchQuery,
      direction: sortDirection,
      onlyUnreviewed: nextValue
    });
  };

  const submitCommentSearch = () => {
    if (!selectedAsset) return;

    loadThreatFeedComments({
      assetId:
        selectedFeedScope === "group-all"
          ? null
          : selectedAsset,

      assetIds:
        selectedFeedScope === "group-all"
          ? selectedAssetIds
          : [],
      offset: 0,
      append: false,
      flag: selectedFlag,
      search: searchQuery,
      direction: sortDirection,
      onlyUnreviewed: unreviewedOnly
    });
  };

  const applyDateRange = (
    nextStartDate = startDate,
    nextEndDate = endDate
  ) => {
    if (!nextStartDate || !nextEndDate) {
      return;
    }

    const parsedStart = new Date(`${nextStartDate}T00:00:00`);
    const parsedEnd = new Date(`${nextEndDate}T23:59:59.999`);

    if (parsedStart >= parsedEnd) {
      setErrorMessage(
        "The start date must be earlier than the end date."
      );
      return;
    }

    setErrorMessage("");

    if (!selectedAsset) {
      return;
    }

    setComments([]);
    setCommentTotal(0);
    setNextOffset(null);

    loadThreatFeedComments({
      assetId:
        selectedFeedScope === "group-all"
          ? null
          : selectedAsset,

      assetIds:
        selectedFeedScope === "group-all"
          ? selectedAssetIds
          : [],
      offset: 0,
      append: false,
      flag: selectedFlag,
      search: searchQuery,
      direction: sortDirection,
      onlyUnreviewed: unreviewedOnly,
      selectedStartDate: nextStartDate,
      selectedEndDate: nextEndDate
    });
  };

  const handleStartDateChange = (event) => {
    const nextStartDate = event.target.value;

    setStartDate(nextStartDate);
    applyDateRange(nextStartDate, endDate);
  };

  const handleEndDateChange = (event) => {
    const nextEndDate = event.target.value;

    setEndDate(nextEndDate);
    applyDateRange(startDate, nextEndDate);
  };

  const toggleThreatSort = () => {
    const nextDirection =
      sortDirection === "desc"
        ? "asc"
        : "desc";

    setSortDirection(nextDirection);

    if (selectedAsset) {
      loadThreatFeedComments({
        assetId:
          selectedFeedScope === "group-all"
            ? null
            : selectedAsset,

        assetIds:
          selectedFeedScope === "group-all"
            ? selectedAssetIds
            : [],
        offset: 0,
        append: false,
        flag: selectedFlag,
        search: searchQuery,
        direction: nextDirection,
        onlyUnreviewed: unreviewedOnly
      });
    }
  };

  const handleFlagSelection = (flagName) => {
    const nextFlag =
      selectedFlag === flagName
        ? ""
        : flagName;

    setSelectedFlag(nextFlag);

    if (selectedAsset) {
      loadThreatFeedComments({
        assetId:
          selectedFeedScope === "group-all"
            ? null
            : selectedAsset,

        assetIds:
          selectedFeedScope === "group-all"
            ? selectedAssetIds
            : [],
        offset: 0,
        append: false,
        flag: nextFlag,
        search: searchQuery,
        direction: sortDirection,
        onlyUnreviewed: unreviewedOnly
      });
    }
  };

  const loadMoreComments = () => {
    if (!selectedAsset || nextOffset === null) {
      return;
    }

    loadThreatFeedComments({
      assetId:
        selectedFeedScope === "group-all"
          ? null
          : selectedAsset,

      assetIds:
        selectedFeedScope === "group-all"
          ? selectedAssetIds
          : [],
      offset: nextOffset,
      append: true,
      flag: selectedFlag,
      search: searchQuery,
      direction: sortDirection,
      onlyUnreviewed: unreviewedOnly
    });
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((current) => {
      const next = new Set(current);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  };

  const markCommentFalsePositive = async (
    commentId,
    event
  ) => {
    event?.stopPropagation();

    await submitCommentEvaluation({
      commentId,
      assessmentType: "False Positive",
      concernReason: [],
      mitigationReason: []
    });
  };

  const toggleCommentFlags = (commentId, event) => {
    event.stopPropagation();

    setExpandedCommentFlags((current) => {
      const next = new Set(current);

      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }

      return next;
    });
  };

  const submitCommentEvaluation = async ({
    commentId,
    assessmentType,
    concernReason = [],
    mitigationReason = [],
    other = ""
  }) => {
    if (!userToken || !commentId) {
      return false;
    }

    try {
      const response = await fetch(
        THREAT_EVALUATION_URL,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            userToken,
            assessmentType,
            concernReason,
            mitigationReason,
            other,
            comId: commentId
          })
        }
      );

      if (response.status === 403) {
        navigate("/login");
        return false;
      }

      if (!response.ok) {
        throw new Error(
          `Threat evaluation request failed: ${response.status}`
        );
      }

      const result = await response.json();

      if (result.message !== "Success") {
        throw new Error(
          result.message || "Evaluation was not saved."
        );
      }

      setComments((current) => {
        if (unreviewedOnly) {
          return current.filter(
            (comment) =>
              comment.id !== commentId
          );
        }

        return current.map((comment) =>
          comment.id === commentId
            ? {
              ...comment,
              status: assessmentType,
              evalType: assessmentType,
              isReviewed: true,
              concernReason,
              mitigations:
                mitigationReason,
              analystNote: other,
              evalDate:
                new Date().toISOString()
            }
            : comment
        );
      });

      if (unreviewedOnly) {
        setCommentTotal((current) =>
          Math.max(0, current - 1)
        );

        setModalIndex(-1);
      }

      return true;
    } catch (error) {
      console.error(
        "Unable to submit comment evaluation:",
        error
      );

      setErrorMessage(
        "Unable to save the comment evaluation."
      );

      return false;
    }
  };


  if (userToken === null || groupsLoading) {
    return (
      <main className="threat-feed-page">
        <div className="empty-state">
          <h3>Loading threat feed</h3>
          <p>
            Loading monitored groups and assets.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="threat-feed-page">
      <header className="threat-feed-header">
        <div>
          <p className="eyebrow">Protective intelligence workspace</p>
          <h1>Threat Feed</h1>
          <p className="header-description">
            Choose a monitored asset to retrieve comments on demand. Groups and assets load first, keeping the workspace fast and responsive.
          </p>
        </div>
        <div className="header-stat">
          <span>Monitoring coverage</span>
          <strong>{totalAssets}</strong>
          <small>{groups.length} groups configured</small>
        </div>
      </header>

      <section className="filter-panel" aria-labelledby="filter-panel-heading">
        <button
          type="button"
          className="filter-panel-toggle"
          onClick={() => setShowFilters((current) => !current)}
          aria-expanded={showFilters}
          aria-controls="threat-feed-filters"
        >
          <span className="filter-toggle-icon" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>

          <span className="filter-toggle-text">
            <strong id="filter-panel-heading">Filters</strong>
            <small>
              {selectedAsset
                ? `${isGroupFeedSelected
                  ? `${selectedGroupRecord?.name || selectedAssetName} · Group feed`
                  : selectedAssetName
                }${selectedFlag
                  ? ` · ${FLAG_LABELS[selectedFlag] ||
                  selectedFlag
                  }`
                  : ""
                }${unreviewedOnly
                  ? " · Unreviewed only"
                  : ""
                }`
                : "Search, flags, groups, and assets"}
            </small>
          </span>

          <span className="filter-toggle-status">
            {(
              selectedFlag ||
              selectedGroup ||
              selectedAsset ||
              searchQuery ||
              unreviewedOnly
            ) && (
                <span className="filter-active-indicator">
                  Active
                </span>
              )}

            {showFilters
              ? <ExpandLessIcon fontSize="small" />
              : <ExpandMoreIcon fontSize="small" />}
          </span>
        </button>

        <div
          id="threat-feed-filters"
          className={`filter-panel-content ${showFilters ? "open" : "closed"}`}
        >
          <section className="feed-toolbar" aria-label="Threat feed controls">
            <div className="search-control">
              <SearchIcon fontSize="small" />
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(event) =>
                  setSearchQuery(event.target.value)
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    submitCommentSearch();
                  }
                }}
                disabled={!selectedAsset}
                placeholder={
                  selectedAsset === "all"
                    ? "Search comments across all assets"
                    : selectedAsset
                      ? "Search comments for selected asset"
                      : "Select an asset before searching"
                }
                aria-label={
                  selectedAsset === "all"
                    ? "Search comments across all assets"
                    : "Search selected asset comments"
                }
              />
            </div>
            <div className="date-window-control">
              <div className="date-window-field">
                <label htmlFor="threat-feed-start-date">
                  From
                </label>

                <input
                  id="threat-feed-start-date"
                  type="date"
                  value={startDate}
                  max={endDate}
                  onChange={handleStartDateChange}
                  disabled={!selectedAsset || commentsLoading}
                  aria-label="Threat feed start date"
                />
              </div>

              <span
                className="date-window-separator"
                aria-hidden="true"
              >
                to
              </span>

              <div className="date-window-field">
                <label htmlFor="threat-feed-end-date">
                  Through
                </label>

                <input
                  id="threat-feed-end-date"
                  type="date"
                  value={endDate}
                  min={startDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={handleEndDateChange}
                  disabled={!selectedAsset || commentsLoading}
                  aria-label="Threat feed end date"
                />
              </div>
            </div>

            <button
              className="secondary-button"
              onClick={toggleThreatSort}
              disabled={!selectedAsset || commentsLoading}
            >
              <FilterListIcon fontSize="small" />
              {sortDirection === "desc"
                ? "Most concerning first"
                : "Least concerning first"}
            </button>
            <button
              className="primary-button"
              onClick={submitCommentSearch}
              disabled={!selectedAsset || commentsLoading}
            >
              {commentsLoading
                ? "Loading..."
                : "Apply search"}
            </button>
          </section>

          <section
            className="review-filter-section"
            aria-labelledby="review-filter-heading"
          >
            <div className="review-filter-copy">
              <h3
                id="review-filter-heading"
                className="filter-subheading"
              >
                Review status
              </h3>

              <p>
                Show only comments that have not received an
                analyst evaluation.
              </p>
            </div>

            <label className="review-filter-toggle">
              <input
                type="checkbox"
                checked={unreviewedOnly}
                onChange={handleUnreviewedToggle}
                disabled={!selectedAsset || commentsLoading}
              />

              <span
                className="review-toggle-track"
                aria-hidden="true"
              >
                <span className="review-toggle-thumb" />
              </span>

              <span className="review-toggle-label">
                Unreviewed only
              </span>
            </label>
          </section>

          <section
            className="flag-filter-section"
            aria-labelledby="flag-filter-heading"
          >
            <div className="section-heading-row">
              <div>
                <h3
                  id="flag-filter-heading"
                  className="filter-subheading"
                >
                  Flags
                </h3>
              </div>
              {(
                selectedFlag ||
                selectedGroup ||
                selectedAsset ||
                searchQuery ||
                unreviewedOnly
              ) && (
                  <button
                    className="text-button"
                    onClick={clearFilters}
                  >
                    Clear filters
                  </button>
                )}
            </div>
            <div className="flag-list">
              {flags.slice(0, showAllFlags ? flags.length : 6).map(([flag]) => (
                <button
                  key={flag}
                  className={`flag-chip ${selectedFlag === flag ? "active" : ""}`}
                  onClick={() => handleFlagSelection(flag)}
                >
                  <span>{FLAG_LABELS[flag] || flag}</span>
                </button>
              ))}

              {flags.length > 6 && (
                <button
                  type="button"
                  className="show-flags-button"
                  onClick={() => setShowAllFlags((current) => !current)}
                >
                  {showAllFlags
                    ? "Show fewer"
                    : `More flags (${flags.length - 6})`}
                  {showAllFlags
                    ? <ExpandLessIcon fontSize="small" />
                    : <ExpandMoreIcon fontSize="small" />}
                </button>
              )}
            </div>
          </section>

          <section className="group-overview" aria-labelledby="group-overview-heading">
            <div className="section-heading-row group-overview-heading">
              <div>
                <h3
                  id="group-overview-heading"
                  className="filter-subheading"
                >
                  Monitoring scopes
                </h3>

                <p className="group-overview-description">
                  Select all monitored assets, a group scope, or an individual asset.
                </p>
              </div>

              <span className="quiet-label">
                Comments load on demand
              </span>
            </div>

            <div className="group-grid">
              <article
                className={`all-assets-card ${selectedAsset === "all"
                  ? "active"
                  : ""
                  }`}
              >
                <button
                  type="button"
                  className="all-assets-action"
                  onClick={openFeedForAllAssets}
                >
                  <span className="all-assets-copy">
                    <span className="scope-label">
                      Global scope
                    </span>

                    <strong>All Monitored Assets</strong>

                    <small>
                      Search comments across every configured group and asset.
                    </small>
                  </span>

                  <span className="all-assets-meta">
                    <span>
                      <strong>{totalAssets}</strong>
                      <small>assets</small>
                    </span>

                    <span className="all-assets-open">
                      View all comments
                      <KeyboardArrowRightIcon fontSize="small" />
                    </span>
                  </span>
                </button>
              </article>

              {groups.map((group) => {
                const expanded = expandedGroups.has(group.id);
                return (
                  <article
                    className={`group-card ${isGroupFeedSelected &&
                      selectedGroup === group.id
                      ? "group-feed-active"
                      : ""
                      } ${isEntireGroupSelected &&
                        selectedGroup === group.id
                        ? "entire-group-active"
                        : ""
                      }`}
                    key={group.id}
                  >
                    <div className="group-card-header">
                      <div className="group-card-identity">
                        <button
                          type="button"
                          className="expand-button"
                          onClick={() => toggleGroup(group.id)}
                          aria-label={`${expanded ? "Collapse" : "Expand"
                            } ${group.name}`}
                          aria-expanded={expanded}
                        >
                          {expanded
                            ? <ExpandLessIcon />
                            : <ExpandMoreIcon />}
                        </button>

                        <button
                          type="button"
                          className="group-summary"
                          onClick={() => openFeedForGroup(group.id)}
                        >
                          <span className="scope-label">
                            Monitoring group
                          </span>

                          <span className="group-name">
                            {group.name}
                          </span>

                          {group.description && (
                            <span className="group-description">
                              {group.description}
                            </span>
                          )}
                        </button>
                      </div>

                      <div className="group-card-details">
                        <div className="group-metrics">
                          <span>
                            <strong>{group.assetCount}</strong>
                            <small>
                              {group.assetCount === 1
                                ? "monitored asset"
                                : "monitored assets"}
                            </small>
                          </span>

                          <span>
                            <strong>{group.searchTermCount}</strong>
                            <small>asset search terms</small>
                          </span>

                          <span>
                            <strong>
                              {group.mainGroupSearchTermCount}
                            </strong>
                            <small>group search terms</small>
                          </span>
                        </div>

                        <div className="group-feed-actions">
                          <button
                            type="button"
                            className="group-feed-button group-feed-primary"
                            onClick={() =>
                              openFeedForGroupComments(
                                group,
                                "group-all"
                              )
                            }
                            disabled={
                              group.allAssetIds.length === 0 ||
                              commentsLoading
                            }
                            title={`View group terms and all assets in ${group.name}`}
                          >
                            <span>View Group Feed</span>
                            <KeyboardArrowRightIcon fontSize="small" />
                          </button>

                          <button
                            type="button"
                            className="group-feed-button group-feed-secondary"
                            onClick={() =>
                              openFeedForGroupComments(
                                group,
                                "group-main"
                              )
                            }
                            disabled={
                              !group.mainGroupAssetId ||
                              commentsLoading
                            }
                            title={
                              group.mainGroupAssetId
                                ? `View only the group-level search terms for ${group.name}`
                                : "No group-level monitoring record is configured"
                            }
                          >
                            <span>Group Terms Only</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {expanded && (
                      <div className="asset-panel">
                        <div className="asset-panel-heading">
                          <span>Monitored assets</span>
                          <small>
                            Select an asset to review its individual feed
                          </small>
                        </div>

                        <div className="asset-list">
                          {group.assets.map((asset) => {
                            return (
                              <button
                                className={`asset-row ${selectedFeedScope === "asset" &&
                                  selectedAsset === asset.id
                                  ? "active"
                                  : ""
                                  }`}
                                key={asset.id}
                                onClick={() =>
                                  openFeedForAsset(
                                    group.id,
                                    asset.id,
                                    asset.name
                                  )
                                }
                              >
                                <span className="asset-row-copy">
                                  <strong>{asset.name}</strong>

                                  <small>
                                    {asset.type} ·{" "}
                                    {asset.searchTermCount}{" "}
                                    {asset.searchTermCount === 1
                                      ? "search term"
                                      : "search terms"}
                                  </small>
                                </span>

                                <KeyboardArrowRightIcon
                                  className="asset-row-arrow"
                                  fontSize="small"
                                />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      </section>

      {showFeed && (
        <section className="comment-feed" aria-labelledby="comment-feed-heading">
          <div className="comment-section-divider">
            <span>Analyst review queue</span>
          </div>
          <div className="section-heading-row feed-heading">
            <div>
              <h2 id="comment-feed-heading">Comments</h2>
            </div>
            <div className="comment-heading-actions">
              <button
                type="button"
                className="change-filters-button"
                onClick={() => setShowFilters(true)}
              >
                <FilterListIcon fontSize="small" />
                Change filters
              </button>

              <span className="result-count">
                {commentsLoading && comments.length === 0
                  ? "Loading..."
                  : `${comments.length} of ${commentTotal} results`}
              </span>
            </div>
          </div>

          <div className="active-filter-row">
            {selectedGroup && (
              <span>
                Group:{" "}
                {selectedGroup === "all"
                  ? "All assets"
                  : groups.find(
                    (group) => group.id === selectedGroup
                  )?.name}
              </span>
            )}
            {selectedAsset && (
              <span>
                {isGroupFeedSelected
                  ? "Scope"
                  : "Asset"}
                :{" "}
                {selectedFeedScope === "group-all"
                  ? "Entire group"
                  : selectedFeedScope === "group-main"
                    ? "Group terms only"
                    : selectedAssetName}
              </span>
            )}
            {selectedFlag && <span>Flag: {FLAG_LABELS[selectedFlag] || selectedFlag}</span>}
            {searchQuery && (
              <span>
                Search: “{searchQuery}”
              </span>
            )}

            <span>
              Date window:{" "}
              {new Intl.DateTimeFormat("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric"
              }).format(new Date(`${startDate}T00:00:00`))}
              {" – "}
              {new Intl.DateTimeFormat("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric"
              }).format(new Date(`${endDate}T00:00:00`))}
            </span>

            {unreviewedOnly && (
              <span>
                Review status: Unreviewed only
              </span>
            )}
          </div>

          <div className="comment-list">
            {errorMessage && (
              <div className="empty-state">
                <h3>Unable to load threat-feed data</h3>
                <p>{errorMessage}</p>
              </div>
            )}

            {commentsLoading && comments.length === 0 && (
              <div className="empty-state">
                <h3>Loading comments</h3>
                <p>
                  {selectedAsset === "all"
                    ? "Retrieving comments across all monitored assets."
                    : isGroupFeedSelected
                      ? `Retrieving the ${selectedGroupRecord?.name || "selected"} group feed.`
                      : "Retrieving the selected asset’s threat feed."}
                </p>
              </div>
            )}
            {filteredComments.map((comment, index) => {
              const band = threatBand(comment.threatValue);
              return (
                <article className="comment-card" key={comment.id}>
                  <button className="comment-card-open" onClick={() => setModalIndex(index)} aria-label={`Open comment from ${comment.username}`}>
                    <div className="comment-card-topline">
                      <div className="comment-context">
                        <strong>{comment.assetName}</strong>
                        <span className="comment-author">@{comment.username}</span>
                        <span>{comment.groupName}</span>
                        <span>{formatDate(comment.datePosted)}</span>
                      </div>
                      <div className="comment-review-state">
                        <span className={`status-pill status-${comment.status.toLowerCase()}`}>
                          {comment.status}
                        </span>

                        <div className={`threat-value ${band.className}`}>
                          <small>{band.label}</small>
                          <strong>{comment.threatValue}</strong>
                        </div>
                      </div>
                    </div>
                    <p className="comment-text">
                      <HighlightedComment
                        text={comment.comment}
                        commentFlags={comment.flags}
                        flagTerms={flagTerms}
                      />
                    </p>

                    <div className="comment-card-footer">
                      <div className="inline-flags">
                        {comment.flags.length > 0 && (
                          <span className="primary-comment-flag">
                            {FLAG_LABELS[comment.flags[0]] || comment.flags[0]}
                          </span>
                        )}

                        {comment.flags.length > 1 && (
                          <div className="additional-flags">
                            <button
                              type="button"
                              className="additional-flags-button"
                              onClick={(event) => toggleCommentFlags(comment.id, event)}
                              aria-expanded={expandedCommentFlags.has(comment.id)}
                            >
                              +{comment.flags.length - 1} flags
                              {expandedCommentFlags.has(comment.id)
                                ? <ExpandLessIcon fontSize="inherit" />
                                : <ExpandMoreIcon fontSize="inherit" />}
                            </button>

                            {expandedCommentFlags.has(comment.id) && (
                              <div
                                className="additional-flags-menu"
                                onClick={(event) => event.stopPropagation()}
                              >
                                {comment.flags.slice(1).map((flag) => (
                                  <span key={flag}>
                                    {FLAG_LABELS[flag] || flag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {comment.flags.length === 0 && (
                          <span className="no-comment-flags">No flags</span>
                        )}
                      </div>
                      {/* <span className={`status-pill status-${comment.status.toLowerCase()}`}>{comment.status}</span> */}
                    </div>
                  </button>
                  <div className="quick-actions">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setModalIndex(index);
                      }}
                    >
                      Evaluate
                    </button>

                    <button
                      type="button"
                      className="false-positive-action"
                      onClick={(event) =>
                        markCommentFalsePositive(
                          comment.id,
                          event
                        )
                      }
                    >
                      False Positive
                    </button>

                    <a
                      href={comment.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Open original comment"
                    >
                      <LaunchIcon fontSize="small" />
                    </a>
                  </div>
                </article>
              );
            })}

            {!commentsLoading &&
              selectedAsset &&
              filteredComments.length === 0 &&
              !errorMessage && (
                <div className="empty-state">
                  <h3>No comments match these filters</h3>
                  <p>
                    Clear the flag or search filter, or select a
                    different asset.
                  </p>

                  <button
                    className="secondary-button"
                    onClick={clearFilters}
                  >
                    Clear filters
                  </button>
                </div>
              )}

            {nextOffset !== null && (
              <div className="load-more-row">
                <button
                  className="secondary-button"
                  onClick={loadMoreComments}
                  disabled={commentsLoading}
                >
                  {commentsLoading
                    ? "Loading..."
                    : "Load more comments"}
                </button>
              </div>
            )}
          </div>
        </section>

      )}

      <ThreatReviewModal
        isOpen={modalIndex >= 0}
        comments={filteredComments}
        currentIndex={modalIndex}
        onIndexChange={setModalIndex}
        onClose={() => setModalIndex(-1)}
        onAssessment={submitCommentEvaluation}
        flagLabels={FLAG_LABELS}
        renderHighlightedComment={(text, commentFlags) => (
          <HighlightedComment
            text={text}
            commentFlags={commentFlags}
            flagTerms={flagTerms}
          />
        )}
      />
    </main>
  );
};

export default ThreatFeed;
